import { CircularProgress, Grid, Paper } from '@mui/material';
import { ColumnSizingState, Row, RowModel, RowSelectionState } from '@tanstack/react-table';
import { Formik, FormikProps } from 'formik';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionTypes, Container, GetContainersByVoyageParams } from '../../global/adapters/crpAPI';
import { DataGrid, Drawer } from '../../global/components';
import EmptyState from '../../global/components/EmptyState/EmptyState';
import { useLocalTableValues } from '../../global/hooks';
import { useGetContainersByVoyage } from '../adapters/change-commercial-release/queries';
import ChangeCommercialReleaseStepper from '../components/change-commercial-release/ChangeCommercialReleaseStepper';
import ContainerDetailDrawer from '../components/shared/ContainerDetailDrawer';
import { baseShippingLineColumnsWithCheckbox } from '../constants/shared/shippingLineColumns';
import { useGetContainersByDashboardMetric } from '../adapters/dashboard/queries';
import { DashboardMetricType } from 'modules/transport-operator/adapters/dashboard/queries';
import { getDashboardMetricType, isFromDashboard } from 'modules/global/helpers/searchParamsHelper';

const initialFields: GetContainersByVoyageParams = {
  vesselName: '',
  voyageNumber: '',
  imoNumber: '',
  stayNumber: '',
  isArchived: false,
  billOfLadingNumber: '',
  equipmentNumber: '',
};

type Props = React.ComponentPropsWithoutRef<'div'> & {
  commercialReleaseAction: ActionTypes;
};

const ChangeCommercialReleaseVoyage: React.FC<Props> = ({ commercialReleaseAction }) => {
  const { t } = useTranslation(['common', 'validation', 'changeVoyage']);
  const formRef = useRef<FormikProps<GetContainersByVoyageParams>>();

  const [displayGrid, setDisplayGrid] = React.useState(false);
  const [searchPerformed, setSearchPerformed] = React.useState(false);
  const [showDetail, setShowDetail] = React.useState<{
    open: boolean;
    data?: Container;
  }>({
    open: false,
    data: undefined,
  });

  const getRowSelection = React.useRef<(() => RowModel<Container>) | null>(null);
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [selectedRows, setSelectedRows] = React.useState<Row<Container>[] | null>(null);

  const [columnSizing, setColumnSizing] = useLocalTableValues<ColumnSizingState>(
    'column-sizing-change-commercial-release',
    {}
  );

  const handleOnSubmit = () => {
    setDisplayGrid(true);
    setSearchPerformed(true);
  };

  const handleCellClick = (myRowData: Container) => {
    setShowDetail({ open: true, data: myRowData });
  };

  React.useEffect(() => {
    if (getRowSelection.current !== null) {
      setSelectedRows(getRowSelection.current().rows);
    }
  }, [rowSelection]);

  const containersByDashboardMetric = useGetContainersByDashboardMetric(
    getDashboardMetricType() as DashboardMetricType,
    isFromDashboard()
  );

  const containersByVoyage = useGetContainersByVoyage({
    isArchived: formRef?.current?.values.isArchived,
    vesselName: formRef?.current?.values?.vesselName,
    voyageNumber: formRef?.current?.values?.voyageNumber,
    imoNumber: formRef?.current?.values?.imoNumber,
    stayNumber: formRef?.current?.values?.stayNumber,
    billOfLadingNumber: formRef?.current?.values.billOfLadingNumber,
    equipmentNumber: formRef?.current?.values.equipmentNumber,
    ...((commercialReleaseAction === 'BLOCK' || commercialReleaseAction === 'UNBLOCK') && {
      action: commercialReleaseAction,
    }),
  });

  const { data, isLoading, isFetching, isError } =
    isFromDashboard(true) && !searchPerformed ? containersByDashboardMetric : containersByVoyage;

  React.useEffect(() => {
    if (!isFetching && data) {
      setDisplayGrid(true);
    }
  }, [isFetching, data]);

  return (
    <Grid container spacing={2} style={{ margin: '20px 0 0 5px' }}>
      <Grid size={{ xs: 4, sm: 3 }}>
        <Formik
          initialValues={initialFields}
          innerRef={formRef as React.Ref<FormikProps<GetContainersByVoyageParams>>}
          onSubmit={handleOnSubmit}
        >
          {({
            submitForm,
            values,
            resetForm,
            errors,
          }: FormikProps<GetContainersByVoyageParams>) => {
            return (
              <ChangeCommercialReleaseStepper
                commercialReleaseAction={commercialReleaseAction}
                errors={errors}
                selectedRows={selectedRows}
                setRowSelection={setRowSelection}
                setDisplayGrid={setDisplayGrid}
                submitForm={submitForm}
                data={data}
                values={values}
                isFetching={isFetching}
                resetForm={resetForm}
                skipSearchStep={isFromDashboard(true)}
              />
            );
          }}
        </Formik>
      </Grid>
      <Grid size={{ xs: 8, sm: 9 }}>
        {isFetching ? (
          <CircularProgress />
        ) : isError ? (
          <EmptyState title={t('validation:errorGetContainers')} />
        ) : displayGrid && data ? (
          <React.Fragment>
            <Paper>
              <DataGrid.Client
                columns={baseShippingLineColumnsWithCheckbox}
                columnSizing={columnSizing}
                data={data}
                getRowSelection={getRowSelection}
                isLoading={isLoading}
                onColumnSizingChange={setColumnSizing}
                onRowSelectionChange={setRowSelection}
                rowSelection={rowSelection}
                onCellClick={handleCellClick}
              />
            </Paper>
            <Drawer setShowDrawer={setShowDetail} showDrawer={showDetail.open}>
              {showDetail.data !== undefined && (
                <ContainerDetailDrawer containerId={showDetail.data.releaseId} />
              )}
            </Drawer>
          </React.Fragment>
        ) : (
          <EmptyState
            title={t('changeVoyage:emptyState.title')}
            subTitle={t('changeVoyage:emptyState.subTitle')}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default ChangeCommercialReleaseVoyage;
